import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { FilterChips } from '~/components/FilterChips';
import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useUser } from '~/modules/auth/hooks/useUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { USER_TYPES } from '~/modules/settings/constants/Filter';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import { UserFormFilter } from '~/modules/settings/modules/user/components/UserFormFilter';
import type { UserListItem } from '~settings/api/lib/schemas';
import { useUserList } from '~settings/api/queries/userList';
import { FilterDrawer } from '~settings/components/FilterDrawer';
import { ListControls } from '~settings/components/ListControls';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { useDefaultSort } from '~settings/hooks/useDefaultSort';
import { translateOptions } from '~settings/utils/helpers';
import { RolesPriority } from '~settings/utils/roles.ts';

import { useUsersColumns } from './hooks/useUsersColumns';

const userEmptyArray: UserListItem[] = [];

const DEFAULT_FILTER = { isActive: 'true' };

export function ListUserPage() {
  const [
    isInternalUserUpdatesEnabled,
    manageExternalUserEnabled,
    labelAdminUserCreationEnabled,
  ] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
    FEATURE_FLAGS.ContainerSettingsManageExternalUser,
    FEATURE_FLAGS.ContainerSettingsLabelAdminUserCreation,
  ]);

  const { hasPermission, isInternal } = useUser();

  // TODO: dodgy isInternal check should be removed once the labelAdminUserCreation
  // feature flag is enabled for all users and archived.
  const canCreateUser =
    hasPermission('USER.CREATE') &&
    manageExternalUserEnabled &&
    (isInternal || labelAdminUserCreationEnabled);

  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { searchTerm } = useSearch();
  const [searchParams, setSearchParams] = useSearchParams();

  const availableFilters = isInternalUserUpdatesEnabled
    ? ['userTypes', 'isActive', 'labelGroupIds', 'labelIds', 'roles']
    : ['isActive', 'isInternal', 'labelGroupIds', 'labelIds', 'roles'];

  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters({
    availableFilters,
    defaultFilter: DEFAULT_FILTER,
  });

  const columns = useUsersColumns(filterData);
  const { sorting, setSorting, orderBy } = useDefaultSort({
    defaultSorting: [{ id: 'fullName', desc: false }],
    syncWithQueryParams: true,
  });

  const { isLoading, isFetching, isError, userList } = useUserList(
    {
      skip,
      take,
      ...(orderBy && { orderBy }),
      ...filterData,
      ...(searchTerm && { search: searchTerm }),
    },
    {
      select: (data) => ({
        ...data,
        data: data.data.map((user) => ({
          ...user,
          // parent label with the highest priority role 1st
          labels: [...user.labels].sort((labelA, labelB) => {
            const result =
              Number(labelB.isParentLabel) - Number(labelA.isParentLabel);

            if (!result) {
              // since roles are sorted by priority already(in schema),
              // show label with higher priority role 1st
              return (
                RolesPriority[labelA.roles[0]] - RolesPriority[labelB.roles[0]]
              );
            }

            return result;
          }),
        })),
      }),
    },
  );

  useEffect(() => {
    if (!searchParams.size) {
      setSearchParams(new URLSearchParams(DEFAULT_FILTER));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once page loads for internal user

  const onClick = (row: UserListItem) => {
    navigate(SETTINGS_ROUTES.USER_DETAIL(row.id));
  };

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <section className="py-md">Something went wrong.</section>;
  }

  return (
    <>
      <ListControls
        placeholder={t('user.search')}
        total={userList?.total ?? 0}
        page={page}
        perPage={perPage}
        entity="users"
        onFilterClick={openDrawer}
        setPageNumber={setPageNumber}
        toolbarRightAddon={
          canCreateUser ? (
            <Button
              asChild
              variant="primary"
              className="shrink-0 self-center"
              size="large"
            >
              <Link to={SETTINGS_ROUTES.USER_CREATE}>{t('user.addLink')}</Link>
            </Button>
          ) : null
        }
        internalFilter={
          availableFilters.includes('isInternal') ? (
            <FilterChips
              onOptionSelect={(_, params) => {
                if (isInternalUserUpdatesEnabled) {
                  params.delete('userType');
                }
              }}
              options={translateOptions(USER_TYPES, t)}
              path="isInternal"
            />
          ) : undefined
        }
      />
      <Table
        data={userList?.data ?? userEmptyArray}
        columns={columns}
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{
          isLoading: isFetching,
          prefillRowsNumber: perPage,
        }}
        rowClassName="h-3xl"
        cellClassName="p-sm"
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <UserFormFilter
          onApply={closeDrawer}
          open={drawerOpen}
          options={{ showLabels: true, showLabelGroups: true }}
        />
      </FilterDrawer>
    </>
  );
}
ListUserPage.displayName = 'ListUserPage';
